import type { Block, BlockType, RowLayout, TemplateBlocks, TemplateRow } from '@engage/types';

/* ────────────────────────────────────────────────────────────────────────────
   Pure utility functions for the builder's block-tree manipulation.

   Kept stateless and side-effect-free so the same operations are testable
   in isolation (vitest) and reusable across the BlockList, settings panel,
   and Send-test flow.
   ──────────────────────────────────────────────────────────────────────── */

/** Browser-side cuid-ish id. crypto.randomUUID is fine — block ids are
 *  only used to identify nodes in the tree, never persisted as primary keys. */
let _seq = 0;
export function newId(prefix: string): string {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${rand}_${++_seq}`;
}

/* ── Address types ────────────────────────────────────────────────────── */

/** Three-part address that uniquely locates a block in the tree. */
export interface BlockAddress {
  rowId: string;
  columnId: string;
  blockId: string;
}

export interface RowAddress {
  rowId: string;
}

/* ── Block factories — one per supported block type ───────────────────── */

export function defaultBlock(type: BlockType): Block {
  switch (type) {
    case 'heading':
      return { id: newId('h'), type: 'heading', props: { text: 'New heading', level: 2 } };
    case 'paragraph':
      return { id: newId('p'), type: 'paragraph', props: { html: 'Paragraph text. Edit me.' } };
    case 'image':
      return {
        id: newId('img'),
        type: 'image',
        props: { src: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600', alt: '' },
      };
    case 'button':
      return {
        id: newId('btn'),
        type: 'button',
        props: { label: 'Click me', href: '#', align: 'left' },
      };
    case 'divider':
      return { id: newId('div'), type: 'divider', props: {} };
    case 'spacer':
      return { id: newId('sp'), type: 'spacer', props: { height: 24 } };
    case 'calendar_slot':
      return {
        id: newId('cal'),
        type: 'calendar_slot',
        props: { url: '{{calendar_url}}', label: 'Find a time', description: 'Pick a slot that suits you.' },
      };
    case 'job_card':
      return {
        id: newId('jc'),
        type: 'job_card',
        props: { token: 'job', show_summary: true },
      };
    case 'signature':
      return {
        id: newId('sig'),
        type: 'signature',
        props: { include_phone: true, include_title: true },
      };
    case 'bullet_list':
      return {
        id: newId('ul'),
        type: 'bullet_list',
        props: { items: ['First point', 'Second point', 'Third point'] },
      };
    case 'detail_rows':
      return {
        id: newId('dr'),
        type: 'detail_rows',
        props: { rows: [{ label: 'Label', value: 'Value' }] },
      };
    case 'footer':
      return { id: newId('foot'), type: 'footer', props: {} };
  }
}

/** Pre-built row with N empty columns matching the chosen layout. */
export function defaultRow(layout: RowLayout): TemplateRow {
  const colCount = layout === '1-2' ? 2 : layout === '1-3' ? 3 : 1;
  return {
    id: newId('row'),
    layout,
    columns: Array.from({ length: colCount }, () => ({ id: newId('col'), blocks: [] })),
  };
}

/* ── Mutations — return a new tree, never mutate input ────────────────── */

function mapRows(tree: TemplateBlocks, fn: (row: TemplateRow) => TemplateRow): TemplateBlocks {
  return { ...tree, rows: tree.rows.map(fn) };
}

export function addRow(tree: TemplateBlocks, layout: RowLayout, atIndex?: number): TemplateBlocks {
  const row = defaultRow(layout);
  const rows = [...tree.rows];
  const idx = atIndex !== undefined ? atIndex : rows.length;
  rows.splice(idx, 0, row);
  return { ...tree, rows };
}

export function removeRow(tree: TemplateBlocks, rowId: string): TemplateBlocks {
  return { ...tree, rows: tree.rows.filter((r) => r.id !== rowId) };
}

export function moveRow(tree: TemplateBlocks, rowId: string, direction: 'up' | 'down'): TemplateBlocks {
  const idx = tree.rows.findIndex((r) => r.id === rowId);
  if (idx === -1) return tree;
  const swapWith = direction === 'up' ? idx - 1 : idx + 1;
  if (swapWith < 0 || swapWith >= tree.rows.length) return tree;
  const rows = [...tree.rows];
  [rows[idx], rows[swapWith]] = [rows[swapWith], rows[idx]];
  return { ...tree, rows };
}

export function updateRow(
  tree: TemplateBlocks,
  rowId: string,
  patch: Partial<TemplateRow>,
): TemplateBlocks {
  return mapRows(tree, (r) => (r.id === rowId ? { ...r, ...patch } : r));
}

export function changeRowLayout(tree: TemplateBlocks, rowId: string, layout: RowLayout): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== rowId) return r;
    const targetCols = layout === '1-2' ? 2 : layout === '1-3' ? 3 : 1;
    const currentCols = r.columns.length;
    let columns = r.columns.slice(0, targetCols);
    while (columns.length < targetCols) columns.push({ id: newId('col'), blocks: [] });
    // Going from many → fewer columns: collapse the dropped columns' blocks
    // into the last surviving column so nothing is silently lost.
    if (currentCols > targetCols) {
      const overflow = r.columns.slice(targetCols).flatMap((c) => c.blocks);
      columns = columns.map((c, i) =>
        i === columns.length - 1 ? { ...c, blocks: [...c.blocks, ...overflow] } : c,
      );
    }
    return { ...r, layout, columns };
  });
}

export function addBlock(
  tree: TemplateBlocks,
  rowId: string,
  columnId: string,
  block: Block,
  atIndex?: number,
): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== rowId) return r;
    return {
      ...r,
      columns: r.columns.map((c) => {
        if (c.id !== columnId) return c;
        const blocks = [...c.blocks];
        const idx = atIndex !== undefined ? atIndex : blocks.length;
        blocks.splice(idx, 0, block);
        return { ...c, blocks };
      }),
    };
  });
}

export function removeBlock(tree: TemplateBlocks, addr: BlockAddress): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== addr.rowId) return r;
    return {
      ...r,
      columns: r.columns.map((c) =>
        c.id !== addr.columnId ? c : { ...c, blocks: c.blocks.filter((b) => b.id !== addr.blockId) },
      ),
    };
  });
}

export function duplicateBlock(tree: TemplateBlocks, addr: BlockAddress): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== addr.rowId) return r;
    return {
      ...r,
      columns: r.columns.map((c) => {
        if (c.id !== addr.columnId) return c;
        const idx = c.blocks.findIndex((b) => b.id === addr.blockId);
        if (idx === -1) return c;
        const dup: Block = { ...c.blocks[idx], id: newId(c.blocks[idx].type.slice(0, 3)) };
        const blocks = [...c.blocks];
        blocks.splice(idx + 1, 0, dup);
        return { ...c, blocks };
      }),
    };
  });
}

export function moveBlock(
  tree: TemplateBlocks,
  addr: BlockAddress,
  direction: 'up' | 'down',
): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== addr.rowId) return r;
    return {
      ...r,
      columns: r.columns.map((c) => {
        if (c.id !== addr.columnId) return c;
        const idx = c.blocks.findIndex((b) => b.id === addr.blockId);
        if (idx === -1) return c;
        const swapWith = direction === 'up' ? idx - 1 : idx + 1;
        if (swapWith < 0 || swapWith >= c.blocks.length) return c;
        const blocks = [...c.blocks];
        [blocks[idx], blocks[swapWith]] = [blocks[swapWith], blocks[idx]];
        return { ...c, blocks };
      }),
    };
  });
}

export function updateBlock<T extends Block>(
  tree: TemplateBlocks,
  addr: BlockAddress,
  patch: Partial<T['props']>,
): TemplateBlocks {
  return mapRows(tree, (r) => {
    if (r.id !== addr.rowId) return r;
    return {
      ...r,
      columns: r.columns.map((c) => {
        if (c.id !== addr.columnId) return c;
        return {
          ...c,
          blocks: c.blocks.map((b) =>
            b.id !== addr.blockId ? b : ({ ...b, props: { ...b.props, ...patch } } as Block),
          ),
        };
      }),
    };
  });
}

export function findBlock(tree: TemplateBlocks, addr: BlockAddress): Block | null {
  const row = tree.rows.find((r) => r.id === addr.rowId);
  if (!row) return null;
  const col = row.columns.find((c) => c.id === addr.columnId);
  if (!col) return null;
  return col.blocks.find((b) => b.id === addr.blockId) ?? null;
}

/** Insert a {{token}} into a string at the given caret position. */
export function insertTokenAt(value: string, caret: number, token: string): string {
  const before = value.slice(0, caret);
  const after = value.slice(caret);
  return `${before}{{${token}}}${after}`;
}

/** Empty starter tree — used by "New blank template" path. */
export function emptyTree(subject: string): TemplateBlocks {
  return {
    settings: { subject },
    rows: [
      {
        id: newId('row'),
        layout: '1',
        columns: [
          {
            id: newId('col'),
            blocks: [
              defaultBlock('heading'),
              defaultBlock('paragraph'),
              defaultBlock('button'),
              defaultBlock('signature'),
              defaultBlock('footer'),
            ],
          },
        ],
      },
    ],
  };
}
